import React, { FC } from 'react';

import { Field, Formik, FormikHelpers } from 'formik';
import { StyleSheet } from 'react-native';

import LoadingAnimation from 'components/loading-animation';
import { FormikOTPInput } from 'components/otp-input';
import { Box, Typography } from 'components/ui';
import { VisuallyHidden } from 'components/ui/visually-hidden';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';

const OTP_FIELD_NAME = 'otp-code';

interface IOtpForm {
  errorMessage?: string | React.ReactNode;
  initialCode?: string | null;
  loading?: boolean;
  otpCodeLength?: number;
  onSubmit(otpValues: string): Promise<void>;
}

const OTP_CODE_LENGTH = 6; // the number of digits in a generated code

export const OtpForm: FC<IOtpForm> = ({
  errorMessage,
  initialCode,
  loading,
  otpCodeLength = OTP_CODE_LENGTH,
  onSubmit,
}) => {
  const initialOtpFormValues = { [OTP_FIELD_NAME]: initialCode || '' };
  const { loading: authLoading, otpAuthError } = useAuthContext();

  const otpErrorMessage = errorMessage || otpAuthError;

  const handleOtpCodeSubmit = async (
    formValues: { [OTP_FIELD_NAME]: string },
    { setStatus, setSubmitting }: FormikHelpers<{ [OTP_FIELD_NAME]: string }>
  ) => {
    try {
      await onSubmit(formValues[OTP_FIELD_NAME]);
    } catch {
      setStatus({ success: false });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialOtpFormValues}
      onSubmit={handleOtpCodeSubmit}
      validateOnChange={false}
    >
      {({ isSubmitting, setFieldValue, touched, values }) => (
        <>
          {authLoading || loading || isSubmitting ? (
            <Box
              accessibilityRole="alert"
              accessibilityLabel={t('visuallyHiddenVerifyingOTP')}
              style={styles.loadingContainer}
            >
              <LoadingAnimation />
            </Box>
          ) : (
            <>
              <VisuallyHidden
                accessibilityLabel={t('youHaveSuccessfullySubmittedTheForm')}
                aria-live="polite"
              />
              <Box style={[styles.fieldWrapper, isWeb && styles.fieldWrapperWeb]}>
                <Field
                  name={OTP_FIELD_NAME}
                  forceErrorState={
                    touched[OTP_FIELD_NAME] &&
                    !!otpErrorMessage &&
                    values[OTP_FIELD_NAME].length === otpCodeLength
                  }
                  setFieldValue={setFieldValue}
                  component={FormikOTPInput}
                  disabled={isSubmitting}
                  otpCodeLength={otpCodeLength}
                  autoFocus
                />
                {!!errorMessage && (
                  <Typography
                    accessibilityRole="alert"
                    color={primitive.$error}
                    style={styles.errorMessage}
                    variant="copyTwo"
                  >
                    {errorMessage}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  errorMessage: {
    marginBottom: 6,
    marginHorizontal: 0,
    marginTop: spacing.$2,
  },
  fieldWrapper: {
    marginBottom: spacing.$5,
    marginHorizontal: 0,
    marginTop: spacing.$4,
    paddingHorizontal: spacing.$1,
    paddingVertical: 0,
    width: '100%',
  },
  fieldWrapperWeb: {
    marginVertical: spacing.$8,
    paddingHorizontal: 0,
  },
  loadingContainer: {
    alignItems: 'center',
    height: spacing.$13_5,
    justifyContent: 'center',
    marginHorizontal: 0,
    marginVertical: spacing.$4,
  },
});
